import type { Tone } from "../design/tone";

/**
 * WHAT A SCREEN SAYS WHEN ITS READ DID NOT COME BACK (UX pass, 21 October 2026).
 *
 * Every screen used to show the same red chip for every failure, with the server's sentence in it and no way
 * to try again. So a permission limit read like a crash ("Pay figures are not yours to read." in red, in
 * English, sometimes naming a permission key), and a real failure left the reader stuck. event.clinic's screen
 * tour set the rule this follows: a limit that is not a fault is neutral grey and calm; a failed load is an
 * error with a retry; a permission key is never shown to a person.
 *
 * Pure, so the rule is asserted in test/failure.test.ts rather than inside a component.
 */
export type Failure = { status: number; refusal: string | undefined };

export type FailureView = { tone: Tone; sentence: string; retry: boolean };

/** The status a refusal arrives with, and the server's words for it as `refusal()` translated them. */
export function failureView(failure: Failure, fallback: string, noAccess: string): FailureView {
  if (failure.status === 403) return { tone: "neutral", sentence: noAccess, retry: false };
  // A record that is not the reader's, or no longer there: also a limit, not a fault, and trying again would
  // give the same answer. The server's sentence says which record.
  if (failure.status === 404) return { tone: "neutral", sentence: failure.refusal ?? fallback, retry: false };
  // A sentence that names a permission key ("You do not hold finance.read, …") is the server talking to a
  // developer: the reader gets the screen's own sentence instead.
  const words = failure.refusal && !/\b[a-z]+\.[a-z_]+\b/.test(failure.refusal) ? failure.refusal : fallback;
  return { tone: "failed", sentence: words, retry: true };
}
